import React, { useContext, useMemo, useState } from 'react';
import styles from '../../../../../styles/components/Modal/Employees/components/PermissionGroupTab.module.css';
import { LangContext } from '../../../../../context/LangContext';
import { AllPermissionsModal } from './AllPermissionsModal';

const translations = {
    ru: {
        heading: 'Общие наборы прав',
        subheading: 'Быстро включайте типовые права для сотрудников. После этого список можно донастроить вручную.',
        viewPermissions: 'Смотреть права',
        enabled: 'Включено',
        enable: 'Включить',
        disable: 'Отключить',
    },
    en: {
        heading: 'Common Permission Sets',
        subheading: 'Quickly enable typical employee permissions. You can still fine-tune the list manually afterwards.',
        viewPermissions: 'View permissions',
        enabled: 'Enabled',
        enable: 'Enable',
        disable: 'Disable',
    },
};

export const CommonPermissionGroupsTab = ({
    groups,
    selectedPermissions,
    onToggleGroup,
    groupedPermissions,
    isDark,
}) => {
    const { langSelected } = useContext(LangContext);
    const t = translations[langSelected] || translations.ru;
    const [viewerGroup, setViewerGroup] = useState(null);

    const selectedPermissionSet = useMemo(
        () => new Set(Array.isArray(selectedPermissions) ? selectedPermissions : []),
        [selectedPermissions]
    );

    const permissionByName = useMemo(
        () => Object.values(groupedPermissions || {})
            .flat()
            .reduce((acc, permission) => {
                if (permission?.name) {
                    acc[permission.name] = permission;
                }
                return acc;
            }, {}),
        [groupedPermissions]
    );

    return (
        <div className={`${styles.container} ${isDark ? styles.darkContainer : ''}`}>
            <h2 className={`${isDark ? styles.darkHeading : ''}`}>{t.heading}</h2>
            <p className={`${styles.commonDescription} ${isDark ? styles.darkCommonDescription : ''}`}>
                {t.subheading}
            </p>

            <div className={styles.commonGroupList}>
                {groups.map((group) => {
                    const selectedCount = group.permissions.filter((permissionName) =>
                        selectedPermissionSet.has(permissionName)
                    ).length;
                    const isFullyEnabled = selectedCount === group.permissions.length;
                    const isPartiallyEnabled = selectedCount > 0 && !isFullyEnabled;
                    const modalGroup = {
                        ...group,
                        permissions: group.permissions
                            .map((permissionName) => permissionByName[permissionName])
                            .filter(Boolean),
                    };

                    return (
                        <div
                            key={group.key}
                            className={`${styles.commonGroupCard} ${isDark ? styles.darkCommonGroupCard : ''} ${isFullyEnabled ? styles.commonGroupCardEnabled : styles.commonGroupCardDisabled} ${isPartiallyEnabled ? styles.commonGroupCardPartial : ''}`}
                        >
                            <div className={styles.commonGroupHeader}>
                                <div>
                                    <h3 className={`${styles.commonGroupTitle} ${isDark ? styles.darkCommonGroupTitle : ''}`}>
                                        {group.title?.[langSelected] || group.title?.ru || group.key}
                                    </h3>
                                    <p className={`${styles.commonGroupText} ${isDark ? styles.darkCommonGroupText : ''}`}>
                                        {group.description?.[langSelected] || group.description?.ru || ''}
                                    </p>
                                </div>
                                <span className={`${styles.commonGroupStatus} ${isFullyEnabled ? styles.commonGroupStatusActive : ''} ${isDark ? styles.darkCommonGroupStatus : ''}`}>
                                    {isFullyEnabled ? t.enabled : isPartiallyEnabled ? `${selectedCount}/${group.permissions.length}` : t.enable}
                                </span>
                            </div>

                            <div className={styles.commonGroupFooter}>
                                <button
                                    type="button"
                                    className={`${styles.commonSecondaryButton} ${styles.commonCardButton} ${isDark ? styles.darkCommonSecondaryButton : ''}`}
                                    onClick={() => setViewerGroup(modalGroup)}
                                >
                                    {t.viewPermissions}
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.commonGroupActionButton} ${isFullyEnabled ? styles.commonGroupActionButtonDanger : styles.commonGroupActionButtonSuccess} ${isDark ? styles.darkCommonGroupActionButton : ''}`}
                                    onClick={() => onToggleGroup(group.permissions, !isFullyEnabled)}
                                >
                                    {isFullyEnabled ? t.disable : t.enable}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            <AllPermissionsModal
                isOpen={Boolean(viewerGroup)}
                onClose={() => setViewerGroup(null)}
                groups={viewerGroup ? [viewerGroup] : []}
                title={viewerGroup?.title?.[langSelected] || viewerGroup?.title?.ru || undefined}
                subtitle={viewerGroup?.description?.[langSelected] || viewerGroup?.description?.ru || undefined}
                isDark={isDark}
            />
        </div>
    );
};
